import { useApolloClient, useMutation, useSubscription } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { notification } from 'antd'
import { Worklog, WorklogOrderByInput, WorklogWhereInput } from '../../../generated/graphql'
import { UPDATE_WORKLOG, WORKLOG_CHANGED_SUBSCRIPTION, WORKLOGS } from '../gql/verteOfficeGQL'
import { ACTIVE_WORKLOG_ORDER_BY, resolveActiveWorklog } from '../shared/resolveActiveWorklog'
import { WORKLOG_ID_KEY } from '../components/timeRecordModal'

const TWO_HOURS_MS = 2 * 60 * 60 * 1000

interface WorklogOutputType {
  worklogs: Worklog[]
}

interface WorklogInputType {
  where: WorklogWhereInput
  orderBy: WorklogOrderByInput[]
}

export function useActiveWorklog(userId?: string) {
  const client = useApolloClient()
  const [activeWorklog, setActiveWorklog] = useState<Worklog | undefined>()
  const [loading, setLoading] = useState(false)
  const [autoEndWorklog] = useMutation(UPDATE_WORKLOG, { fetchPolicy: 'no-cache' })
  // Prevents "while you were gone" toast from firing when the subscription already showed a real-time one
  const suppressGoneToastRef = useRef(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    const hadStoredWorklog = !!localStorage.getItem(WORKLOG_ID_KEY)

    try {
      const resolved = await resolveActiveWorklog({
        userId,
        fetchWorklogs: async (where) => {
          const result = await client.query<WorklogOutputType, WorklogInputType>({
            query: WORKLOGS,
            variables: {
              where,
              orderBy: ACTIVE_WORKLOG_ORDER_BY,
            },
            fetchPolicy: 'no-cache',
          })

          return result.data?.worklogs ?? []
        },
        setStoredWorklogId: (worklogId) => localStorage.setItem(WORKLOG_ID_KEY, worklogId),
        clearStoredWorklogId: () => localStorage.removeItem(WORKLOG_ID_KEY),
      })

      // Client-side safety net: auto-end worklogs open for more than 2 hours
      // (runs until the backend cron takes over)
      if (resolved && dayjs().diff(dayjs(resolved.start_time)) > TWO_HOURS_MS) {
        const totalHours = parseFloat(
          (dayjs().diff(dayjs(resolved.start_time), 'minute') / 60).toFixed(2)
        )
        await autoEndWorklog({
          variables: {
            data: {
              status: 'COMPLETE',
              end_time: new Date().toISOString(),
              total_hours: totalHours,
            },
            where: { id: resolved.id },
          },
        })
        localStorage.removeItem(WORKLOG_ID_KEY)
        setActiveWorklog(undefined)
        notification.warning({ message: 'Tu actividad fue finalizada automáticamente por tiempo límite' })
        return
      }

      // "While you were gone" — worklog was closed externally (e.g. by the backend cron)
      if (hadStoredWorklog && !resolved && !suppressGoneToastRef.current) {
        notification.info({ message: 'Tu actividad fue cerrada mientras no estabas en la plataforma' })
      }
      suppressGoneToastRef.current = false

      setActiveWorklog(resolved as Worklog | undefined)
    } catch (error) {
      console.error('Error resolving active worklog:', error)
      setActiveWorklog(undefined)
    } finally {
      setLoading(false)
    }
  }, [client, userId, autoEndWorklog])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Real-time: backend closes the worklog (cron) → subscription fires → instant toast + refresh
  const { data: subscriptionData } = useSubscription(WORKLOG_CHANGED_SUBSCRIPTION)
  useEffect(() => {
    if (!subscriptionData?.worklogChanged) return
    const changed = subscriptionData.worklogChanged
    if (changed.user_id !== userId) return

    if (changed.end_time != null) {
      suppressGoneToastRef.current = true
      notification.warning({ message: 'Tu actividad fue finalizada automáticamente' })
    }
    void refresh()
  }, [subscriptionData, userId, refresh])

  return {
    activeWorklog,
    loading,
    refresh,
  }
}
